// Customer-facing Quote DTOs. The customer-facing surface is a strict
// subset of the Quote / QuoteLine schema — internal-only columns
// (legacy_notes_text, operator_notes_text, import_provenance,
// notes_promotion_log, import_warnings, import_run_id, quote_token,
// client_request_id) are excluded by the serializer (see
// services/quotes/serialize.ts).
// The quote-level Pattern B services Nexus prices as their own lines. None has
// a QuoteLine of its own, so each is persisted as a QuotePricedLine row for
// display continuity. Array ORDER IS DISPLAY ORDER — written to
// QuotePricedLine.display_order at price time so the sort lives at the data
// layer, not in the renderer. Adding a quote-level priced service is a matter
// of adding a scope here and its entry to quotePricedLineSources on the pricing
// side; no schema change, no new serializer, no new render branch.
//
//   expedite       — the rush uplift (EXPEDITE_FAMILY_KEY); quantity always 1.
//   physical_proof — a physical (not digital) proof; quantity always 1.
//   hands_on_prep  — the per-line combined_file_prep flags rolled up by Nexus
//                    into ONE add-on line; quantity = the count of flagged
//                    lines (a 50- and a 500-book line each count as one).
//   artwork_barcode — the barcode Nexus applies on a fulfilled apply_barcode
//                    order, added by Nexus with no portal project behind it;
//                    quantity = the labels it applies.
export const QUOTE_PRICED_LINE_SCOPES = [
    'expedite',
    'physical_proof',
    'hands_on_prep',
    'artwork_barcode',
];
// The placed-order statuses a change order can re-open for edits. A draft or
// priced quote is already editable, and a cancelled, declined or
// signature-pending one is not a deal Nexus can change: awaiting_signature
// holds a portal-sent signature envelope the edit would no longer match.
export const CHANGE_ORDER_EDITABLE_STATUSES = [
    'submitted',
    'signed',
    'pending_approval',
    'approved',
    'paid',
];
export function isChangeOrderEditableStatus(status) {
    return CHANGE_ORDER_EDITABLE_STATUSES.includes(status);
}
// Once a rep authors an approved quote on the Opportunity (control 'sales'), the
// status read syncs the portal order to 'approved'. Nothing is placed yet: the
// partner has not paid or signed, and Nexus keeps the Opportunity editable until
// a signature or a close locks it. So an IMPERSONATING rep keeps editing the order
// exactly as while it was a draft — every save re-imports its lines into the same
// Opportunity, and the next status read re-syncs the total from whatever Nexus
// approves. A partner on their own session sees the order read-only.
//
// `closed` is Nexus's lock verdict; the portal never derives one of its own.
export const SALES_QUOTE_REP_EDITABLE_STATUSES = ['draft', 'priced', 'approved'];
export function isSalesQuoteOpenForRep(quoteStatus, status) {
    return (status.control === 'sales' &&
        !status.closed &&
        SALES_QUOTE_REP_EDITABLE_STATUSES.includes(quoteStatus));
}
// A draft that was minted but never worked on: no lines, no quote-level service
// selected, and never priced. The service clause keeps a draft whose only answers
// are its delivery details from reading as "Not started" beside a Discard offer
// and matching the reaper. Worth
// naming because it drove a UI defect — an empty auto-minted draft rendered in
// the orders list with the same "In progress" treatment as a real order, so a
// customer (and a rep) could not tell twelve accidents from one job. The list
// renders these as "Not started" and offers to discard them; the reaper
// soft-deletes the abandoned ones on the same definition, so the surface and the
// sweep can never disagree about what counts as empty.
export function isEmptyDraft(quote) {
    return (quote.status === 'draft' &&
        quote.line_count === 0 &&
        quote.total_sale_amount === null &&
        !quote.has_order_services);
}
//# sourceMappingURL=quote.js.map